import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner, BrandLoader, Button, Skeleton } from '@acs/ui';
import { walletView, type GetBillingOverviewResponse } from '@acs/shared';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { billingError } from './billingText.js';
import { AutoTopupWidget, type AutoTopupControl } from './AutoTopup.js';
import { BillingFaq, BuyMinutes, SubscribeAndChoose } from './MinutePlan.js';
import { NO_PACKAGE, readSelectedPackage, writeSelectedPackage } from './planMath.js';
import { useBillingOverview, useCheckIncludedMinutes } from './prepaidHooks.js';
import { useI18n } from '../../i18n/index.js';
import { isSubscribed } from '../../shared/lib/subscription.js';
import { mailSupport } from '../../shared/lib/support.js';

/**
 * Activation for a PREPAID business (doc 57, doc 59 N07): the subscription
 * and package widget, with auto top-up under it.
 *
 * Shown in two places, both of which own the heading and the way onward, so
 * nothing here navigates by itself:
 * - the setup wizard's Activate step (doc 63), where the step's Continue also
 *   saves auto top-up (`autoTopup`);
 * - Settings › Billing, for a business that is not subscribed (a lapsed or
 *   cancelled subscription).
 *
 * With an offer that includes minutes, ONE payment activates: the
 * subscription. The paid period's minutes are granted by the server from the
 * verified invoice, never assumed by the browser. Until they are live, the
 * SERVER says why (doc 61 R04: checking, payment not through, a later start,
 * a plan that never includes minutes, held for review) and each state has a
 * way forward — check again, billing, support. A load failure is shown as
 * one, never hidden behind a spinner.
 *
 * Without included minutes (older prepaid wallets) the subscription is
 * followed by a required first package, as before.
 */
export function PrepaidActivation({
  business,
  canceled,
  autoTopup,
}: {
  business: BusinessDoc;
  canceled: boolean;
  /** The wizard's auto top-up form state: the step's Continue saves it. */
  autoTopup?: AutoTopupControl;
}) {
  const { t } = useI18n();
  const overview = useBillingOverview(business);
  const subscribed = isSubscribed(business);
  const view = walletView(business.billingSummary, new Date());
  const pendingOrder = !!business.billingSummary.activeTopupOrderId;
  const offerIncluded = !!business.billingSummary.offerVersion;
  // The package picked before Checkout was paid IN Checkout, together with
  // the subscription (13 Oct 2026). Once subscribed, forget it, so it is never
  // offered — or pre-selected for a second purchase — again.
  useEffect(() => {
    if (subscribed && readSelectedPackage(business.id) !== NO_PACKAGE) writeSelectedPackage(business.id, NO_PACKAGE);
  }, [subscribed, business.id]);

  const funded = subscribed && view.availableSeconds > 0 && !pendingOrder;
  const ov = overview.data;
  // Subscribed to the offer, but its minutes are not live: the server's
  // overview says why (doc 61 R04). Never assumed paid from the projection.
  const waiting = subscribed && offerIncluded && !funded && !pendingOrder && !view.allowanceLive;

  return (
    <div className="subscribe plan">
      {canceled && !subscribed && <Banner tone="info">{t('plan.canceled')}</Banner>}

      {funded ? (
        // The step's own Continue is the way on; more minutes and auto
        // top-up are right below.
        <Banner tone="success">
          {offerIncluded ? t('plan.funded.included', { minutes: Math.floor(view.availableSeconds / 60) }) : t('plan.funded.banner', { minutes: Math.floor(view.availableSeconds / 60) })}
        </Banner>
      ) : overview.isLoading ? (
        <div className="plan-grid">
          <div className="plan-main">
            <Skeleton height={28} width="50%" />
            <Skeleton height={64} />
            <Skeleton height={64} />
            <Skeleton height={64} />
          </div>
          <Skeleton height={220} />
        </div>
      ) : overview.isError || !ov ? (
        <Banner
          tone="danger"
          title={t('prepaid.loadFailed')}
          actions={<Button variant="secondary" onClick={() => overview.refetch()}>{t('prepaid.retry')}</Button>}
        >
          {billingError(t, overview.error)}
        </Banner>
      ) : waiting ? (
        <IncludedStatus business={business} ov={ov} />
      ) : subscribed ? (
        <>
          <Banner tone="success">{t('plan.subscribed.banner')}</Banner>
          <BuyMinutes business={business} ov={ov} sticky />
        </>
      ) : (
        <SubscribeAndChoose business={business} ov={ov} />
      )}

      {/* Before paying it needs no data; after, it shows a skeleton while
          the settings load. Hidden only when the overview failed to load —
          the banner above says so. */}
      {(ov || overview.isLoading || !subscribed) && (
        <AutoTopupWidget ctl={autoTopup} business={business} subscribed={subscribed} loading={!ov} />
      )}

      <BillingFaq includedMinutes={offerIncluded ? Math.floor((ov?.included?.perPeriodSeconds ?? 0) / 60) : 0} />
    </div>
  );
}

/**
 * Why a subscribed offer business has no included minutes yet (doc 61 R04),
 * as decided by the server. Every state names its way forward: "check again"
 * (the server re-reads the latest invoice, at most once a minute), billing,
 * support — and, where calls must run on purchased minutes for now, the
 * optional package picker. A state where no grant can arrive (a legacy plan,
 * a period under review) never shows a spinner.
 */
function IncludedStatus({ business, ov }: { business: BusinessDoc; ov: GetBillingOverviewResponse }) {
  const { t, tag } = useI18n();
  const navigate = useNavigate();
  const check = useCheckIncludedMinutes(business.id);
  const prep = ov.included.preparation ?? 'checking';
  const minutes = Math.floor(ov.included.perPeriodSeconds / 60);
  const day = (iso: string | null | undefined) => (iso ? new Intl.DateTimeFormat(tag, { dateStyle: 'long' }).format(new Date(iso)) : null);
  const checkedAt = ov.included.checkedAt ? new Intl.DateTimeFormat(tag, { timeStyle: 'short' }).format(new Date(ov.included.checkedAt)) : null;
  const nextAt = day(ov.included.nextAt);

  const billing = (
    <Button variant="secondary" onClick={() => navigate(`/b/${business.id}/settings?section=billing`)}>
      {t('plan.prep.openBilling')}
    </Button>
  );
  const support = (
    <Button variant="ghost" onClick={() => mailSupport(`Included minutes — ${business.name} (${business.id})`)}>
      {t('plan.prep.contactSupport')}
    </Button>
  );
  const checkAgain = (
    <Button variant="secondary" loading={check.isPending} disabled={check.isPending} onClick={() => check.mutate()}>
      {t('plan.prep.checkAgain')}
    </Button>
  );
  const feedback = (
    <p className="plan-muted plan-small" aria-live="polite">
      {check.isError
        ? t('plan.prep.checkFailed')
        : check.data && !check.data.checked
          ? t('plan.prep.checkedNothingNew')
          : checkedAt
            ? t('plan.prep.checkedAt', { time: checkedAt })
            : ''}
    </p>
  );
  // Calls run on purchased minutes until the included ones start: offered,
  // never required (doc 61 §5.4).
  const buy = <BuyMinutes business={business} ov={ov} />;

  switch (prep) {
    case 'awaiting_payment':
      return (
        <>
          <Banner tone="warning" title={t('plan.prep.awaitingPayment.title')} actions={<>{billing}{checkAgain}</>}>
            {t('plan.prep.awaitingPayment.body')}
          </Banner>
          {feedback}
        </>
      );
    case 'scheduled':
      return (
        <>
          <Banner tone="info" title={t('plan.prep.scheduled.title', { date: nextAt ?? '' })}>{t('plan.prep.purchasedUntil')}</Banner>
          {buy}
        </>
      );
    case 'next_renewal':
      return (
        <>
          <Banner tone="info" title={t('plan.prep.nextRenewal.title')}>
            {nextAt ? t('plan.prep.nextRenewal.body', { date: nextAt, minutes }) : t('plan.prep.nextRenewal.bodyNoDate', { minutes })}{' '}
            {t('plan.prep.purchasedUntil')}
          </Banner>
          {buy}
        </>
      );
    case 'unsupported_price':
      return (
        <>
          <Banner tone="info" title={t('plan.prep.unsupported.title')} actions={support}>
            {t('plan.prep.unsupported.body')}
          </Banner>
          {buy}
        </>
      );
    case 'review':
      return (
        <>
          <Banner tone="warning" title={t('plan.prep.review.title')} actions={<>{support}{billing}</>}>
            {t('plan.prep.review.body')}
          </Banner>
          {buy}
        </>
      );
    default:
      // `checking` (and anything newer than this build knows).
      return (
        <div className="plan-preparing">
          <BrandLoader size={24} />
          <div>
            <p className="plan-preparing__title" role="status">{t('plan.prep.checking.title')}</p>
            <p className="plan-muted plan-small">{t('plan.prep.checking.body')}</p>
            <div className="plan-actions plan-preparing__actions">
              {checkAgain}
              {billing}
              {support}
            </div>
            {feedback}
          </div>
        </div>
      );
  }
}
